import { Link } from "react-router-dom";
import { BAY_ISLE, TERRACOTTA, SEMI_SWEET, PROPER_GREY, RULE, WHITE, PLASTER, FONT_DISPLAY, FONT_DISPLAY_HEAVY, FONT_SERIF, FONT_BODY } from "../theme";

const VALUES = [
  {
    icon: "ti-bulb",
    title: "Strategy first",
    desc: "Content without strategy is just noise. Every piece of work we produce is grounded in a clear reason for existing — and a clear audience it's meant to reach.",
  },
  {
    icon: "ti-sparkles",
    title: "AI as a tool, not a shortcut",
    desc: "We use AI to handle the production work — the staging, the drafting, the formatting. Creative direction, brand voice, and judgment stay human. Always.",
  },
  {
    icon: "ti-adjustments",
    title: "Boutique by design",
    desc: "We're not an agency with 50 clients on a content assembly line. Every engagement gets real attention. That's not a pitch — it's a constraint we've chosen.",
  },
  {
    icon: "ti-chart-line",
    title: "Built to grow with you",
    desc: "The systems we build aren't one-offs. They're designed to scale — so as your business grows, your marketing infrastructure grows with it.",
  },
];

const PROCESS = [
  {
    num: "01",
    title: "We listen before we build",
    desc: "Every engagement starts with understanding your business, your audience, and where you want to go. We don't assume — we ask.",
  },
  {
    num: "02",
    title: "Strategy shapes everything",
    desc: "Before anything is produced, we map out the why. What's the goal? Who's it for? What does success look like? That clarity drives every decision downstream.",
  },
  {
    num: "03",
    title: "AI handles the production",
    desc: "Virtual staging, listing copy, captions, reels, content calendars — AI produces the volume. We direct it, refine it, and make sure nothing sounds generic.",
  },
  {
    num: "04",
    title: "You get work that's ready to use",
    desc: "No half-finished drafts. No rounds of back-and-forth before something is usable. What we deliver, you can post, print, or send.",
  },
];

function ValueCard({ icon, title, desc }) {
  return (
    <div style={{
      background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "22px 20px",
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: "rgba(195,135,98,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
      }}>
        <i className={`ti ${icon}`} aria-hidden="true" style={{ fontSize: 18, color: TERRACOTTA }} />
      </div>
      <p style={{ fontSize: 14, fontWeight: 700, color: BAY_ISLE, marginBottom: 8, fontFamily: FONT_DISPLAY }}>{title}</p>
      <p style={{ fontSize: 13, color: SEMI_SWEET, lineHeight: 1.7, fontFamily: FONT_BODY }}>{desc}</p>
    </div>
  );
}

function ProcessRow({ num, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 20, paddingBottom: "1.75rem", marginBottom: "1.75rem", borderBottom: `1px solid ${RULE}` }}>
      <p style={{
        fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: 28,
        color: TERRACOTTA, flexShrink: 0, lineHeight: 1, marginTop: 2, minWidth: 36,
      }}>{num}</p>
      <div>
        <p style={{ fontSize: 15, fontWeight: 700, color: BAY_ISLE, marginBottom: 6, fontFamily: FONT_DISPLAY }}>{title}</p>
        <p style={{ fontSize: 13.5, color: SEMI_SWEET, lineHeight: 1.7, fontFamily: FONT_BODY }}>{desc}</p>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div>

      {/* HERO */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "4rem 20px 3rem", textAlign: "center" }}>
        <p style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase",
          color: TERRACOTTA, borderBottom: `1.5px solid ${TERRACOTTA}`,
          display: "inline-block", paddingBottom: 4, marginBottom: 20, fontFamily: FONT_BODY,
        }}>
          About Acadia
        </p>
        <h1 style={{
          fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(28px, 6vw, 46px)",
          color: BAY_ISLE, lineHeight: 1.12, marginBottom: 20, fontWeight: 400,
        }}>
          We don't just market your business —{" "}
          <em style={{ color: TERRACOTTA, fontStyle: "italic", fontFamily: FONT_SERIF, fontSize: "1.05em" }}>
            we build the systems that keep it running
          </em>
        </h1>
        <p style={{ fontSize: 15, color: SEMI_SWEET, lineHeight: 1.8, maxWidth: 520, margin: "0 auto", fontFamily: FONT_BODY }}>
          Acadia is a boutique, AI-powered marketing studio serving real estate agents and small businesses
          in the Hudson Valley and beyond. Strategy first. Content second. Growth follows.
        </p>
      </div>

      {/* BRAND STATEMENT */}
      <div style={{ background: BAY_ISLE, padding: "4rem 20px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <p style={{
            fontFamily: FONT_SERIF, fontStyle: "italic",
            fontSize: "clamp(20px, 3.5vw, 28px)",
            color: WHITE, lineHeight: 1.6, marginBottom: 28, textAlign: "center",
          }}>
            "The smarter way to show up online isn't about doing more.
            It's about doing the right things — with the right tools — so the work actually compounds."
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
            {["Elevate", "Differentiate", "Dominate", "Succeed"].map((word, i) => (
              <p key={i} style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase",
                color: TERRACOTTA, fontFamily: FONT_BODY,
              }}>{word}</p>
            ))}
          </div>
        </div>
      </div>

      {/* WHAT WE ARE */}
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "4rem 20px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "start" }}>
          <div>
            <p style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
              color: PROPER_GREY, marginBottom: 14, fontFamily: FONT_BODY,
            }}>What we are</p>
            <h2 style={{
              fontFamily: FONT_DISPLAY, fontSize: "clamp(22px, 3.5vw, 30px)",
              color: BAY_ISLE, fontWeight: 400, marginBottom: 16, lineHeight: 1.2,
            }}>
              A studio, not an agency
            </h2>
            <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.8, marginBottom: 14, fontFamily: FONT_BODY }}>
              Acadia operates as a boutique studio — intentionally small, intentionally focused.
              We work with a limited number of clients at a time so that every engagement gets the
              attention it deserves.
            </p>
            <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.8, fontFamily: FONT_BODY }}>
              We're not a social media management agency churning out generic content on a schedule.
              We're a marketing partner that uses AI to do the heavy production lifting —
              so the strategy, voice, and results stay sharp.
            </p>
          </div>
          <div>
            <p style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
              color: PROPER_GREY, marginBottom: 14, fontFamily: FONT_BODY,
            }}>What we're not</p>
            <h2 style={{
              fontFamily: FONT_DISPLAY, fontSize: "clamp(22px, 3.5vw, 30px)",
              color: BAY_ISLE, fontWeight: 400, marginBottom: 16, lineHeight: 1.2,
            }}>
              Not a VA. Not a template shop.
            </h2>
            <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.8, marginBottom: 14, fontFamily: FONT_BODY }}>
              We're not here to post on your behalf while you stay out of it, and we're not
              selling plug-and-play templates that look the same for everyone.
            </p>
            <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.8, fontFamily: FONT_BODY }}>
              Every deliverable is built around your brand, your market, and your goals.
              AI makes it fast. Strategy makes it work.
            </p>
          </div>
        </div>
      </div>

      {/* VALUES */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "4rem 20px" }}>
        <p style={{
          textAlign: "center", fontSize: 10, fontWeight: 700, letterSpacing: "0.2em",
          textTransform: "uppercase", color: PROPER_GREY, marginBottom: 10, fontFamily: FONT_BODY,
        }}>
          How we work
        </p>
        <h2 style={{
          textAlign: "center", fontFamily: FONT_DISPLAY,
          fontSize: "clamp(22px, 4vw, 30px)", color: BAY_ISLE, fontWeight: 400, marginBottom: 32,
        }}>
          The principles behind everything we build
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
          {VALUES.map((v, i) => <ValueCard key={i} {...v} />)}
        </div>
      </div>

      {/* PROCESS */}
      <div style={{ background: WHITE, padding: "4rem 20px" }}>
        <div style={{ maxWidth: 660, margin: "0 auto" }}>
          <p style={{
            textAlign: "center", fontSize: 10, fontWeight: 700, letterSpacing: "0.2em",
            textTransform: "uppercase", color: PROPER_GREY, marginBottom: 10, fontFamily: FONT_BODY,
          }}>
            Our process
          </p>
          <h2 style={{
            textAlign: "center", fontFamily: FONT_DISPLAY,
            fontSize: "clamp(22px, 4vw, 30px)", color: BAY_ISLE, fontWeight: 400, marginBottom: 36,
          }}>
            From first conversation to finished work
          </h2>
          {PROCESS.map((p, i) => <ProcessRow key={i} {...p} />)}
        </div>
      </div>

      {/* WHY US VS DIY AI */}
      <div style={{ background: WHITE, padding: "4rem 20px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <p style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
            color: PROPER_GREY, marginBottom: 14, fontFamily: FONT_BODY, textAlign: "center",
          }}>
            The honest answer
          </p>
          <h2 style={{
            fontFamily: FONT_DISPLAY, fontSize: "clamp(22px, 4vw, 32px)",
            color: BAY_ISLE, fontWeight: 400, marginBottom: 14, lineHeight: 1.2, textAlign: "center",
          }}>
            "Why not just use AI myself?"
          </h2>
          <p style={{
            fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: 17,
            color: SEMI_SWEET, lineHeight: 1.7, textAlign: "center", maxWidth: 540,
            margin: "0 auto 40px", fontWeight: 400,
          }}>
            You could. But you probably shouldn't have to.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16 }}>
            {[
              {
                icon: "ti-refresh",
                title: "AI changes constantly",
                desc: "The tools that worked last month may not work the same way this month. Keeping up with what's new, what's better, and what's actually worth using is a job in itself. That's our job — not yours.",
              },
              {
                icon: "ti-code",
                title: "The tech gets frustrating fast",
                desc: "JSON documents, prompt engineering, API configs, HTML tweaks — even the best AI tools fight back sometimes. You're a real estate or business expert. You shouldn't be wrestling with robots to get a caption written.",
              },
              {
                icon: "ti-clock",
                title: "Your time is worth more",
                desc: "The hour you spend fighting with ChatGPT to get a listing description right is an hour you're not spending with a client, on a showing, or closing a deal. We do the AI work so you don't have to.",
              },
              {
                icon: "ti-target",
                title: "Knowing what to ask is a skill",
                desc: "Everyone has access to the same tools. What makes the output actually good is knowing how to direct it — what to prompt, what to cut, and when to throw it out entirely. That's what we bring.",
              },
            ].map((item, i) => (
              <div key={i} style={{
                background: "rgba(33,62,68,0.03)", border: `1px solid ${RULE}`,
                borderRadius: 14, padding: "22px 20px",
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: "rgba(195,135,98,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
                }}>
                  <i className={`ti ${item.icon}`} aria-hidden="true" style={{ fontSize: 18, color: TERRACOTTA }} />
                </div>
                <p style={{ fontSize: 14, fontWeight: 700, color: BAY_ISLE, marginBottom: 8, fontFamily: FONT_DISPLAY }}>{item.title}</p>
                <p style={{ fontSize: 13, color: SEMI_SWEET, lineHeight: 1.75, fontFamily: FONT_BODY }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: BAY_ISLE, padding: "3.5rem 20px", textAlign: "center" }}>
        <p style={{
          fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: "clamp(18px, 3vw, 24px)",
          color: WHITE, marginBottom: 8, maxWidth: 480, margin: "0 auto 8px",
        }}>
          Ready to work smarter?
        </p>
        <p style={{ fontSize: 13, color: "rgba(242,238,234,0.6)", marginBottom: 24, fontFamily: FONT_BODY }}>
          Tell us about your business and what you're looking for.
        </p>
        <Link to="/get-started" style={{
          textDecoration: "none", display: "inline-block", padding: "14px 28px",
          borderRadius: 9, background: TERRACOTTA, color: WHITE,
          fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
          fontFamily: FONT_BODY,
        }}>
          Get Started →
        </Link>
      </div>

    </div>
  );
}
